import React, { useState, useEffect, useCallback, useRef } from 'react';

const AUTH_TOKEN_KEY = 'admin_auth_token';
const AUTH_USER_KEY = 'admin_auth_user';

interface User {
  email: string;
  name?: string;
  picture?: string;
}

interface AdminAuthProps {
  children: React.ReactNode;
}

// Helper functions to get/set/clear auth - exported for use in api.ts
export function getAuthToken(): string | null {
  return localStorage.getItem(AUTH_TOKEN_KEY);
}

export function getAuthUser(): User | null {
  const user = localStorage.getItem(AUTH_USER_KEY);
  return user ? JSON.parse(user) : null;
}

export function clearAuth(): void {
  localStorage.removeItem(AUTH_TOKEN_KEY);
  localStorage.removeItem(AUTH_USER_KEY);
}

function AdminAuth({ children }: AdminAuthProps) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [googleClientId, setGoogleClientId] = useState<string | null>(null);

  // Fetch Google Client ID from backend (more secure than env var)
  useEffect(() => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (clientId) {
      setGoogleClientId(clientId);
    } else {
      setError('Google OAuth not configured');
      setLoading(false);
    }
  }, []);

  // Verify existing token validity
  const verifyExistingToken = useCallback(async () => {
    const token = getAuthToken();
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const res = await fetch('/api/auth/verify', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        setUser(data.user);
        setIsAuthenticated(true);
      } else {
        // Token invalid, clear it
        clearAuth();
      }
    } catch {
      clearAuth();
    } finally {
      setLoading(false);
    }
  }, []);

  // Initialize Google Sign-In when client ID is loaded
  useEffect(() => {
    if (!googleClientId) return;

    // Check for existing valid token first
    verifyExistingToken();
  }, [googleClientId, verifyExistingToken]);

  const handleGoogleCallback = useCallback(async (response: { credential: string }) => {
    console.log('=== GOOGLE CALLBACK RECEIVED ===');
    console.log('Response:', response);
    setError('');
    setLoading(true);

    try {
      const res = await fetch('/api/auth/google', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ credential: response.credential })
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.error || 'Authentication failed');
        setLoading(false);
        return;
      }

      // Store token and user info in localStorage (persistent)
      localStorage.setItem(AUTH_TOKEN_KEY, data.token);
      localStorage.setItem(AUTH_USER_KEY, JSON.stringify(data.user));

      setUser(data.user);
      setIsAuthenticated(true);
    } catch {
      setError('Authentication failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  const [buttonRendered, setButtonRendered] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Initialize Google Sign-In button when ready
  useEffect(() => {
    if (!googleClientId || isAuthenticated || loading) return;

    let attempts = 0;
    const maxAttempts = 10;
    let timeoutId: NodeJS.Timeout;

    // Wait for Google script to load with exponential backoff
    const initializeGoogle = () => {
      attempts++;

      if (typeof window !== 'undefined' && (window as any).google?.accounts?.id) {
        try {
          console.log('Initializing Google Sign-In with client ID:', googleClientId);
          (window as any).google.accounts.id.initialize({
            client_id: googleClientId,
            callback: handleGoogleCallback,
          });

          const buttonContainer = document.getElementById('google-signin-button');
          if (buttonContainer) {
            // Clear any previous content
            buttonContainer.innerHTML = '';

            (window as any).google.accounts.id.renderButton(buttonContainer, {
              theme: 'filled_blue',
              size: 'large',
              width: 280,
              text: 'signin_with',
            });

            // Check if button was actually rendered (Google adds content to the div)
            setTimeout(() => {
              if (buttonContainer.children.length > 0) {
                setButtonRendered(true);
              }
            }, 100);
          }
        } catch (err) {
          console.error('Google Sign-In initialization error:', err);
        }
      } else if (attempts < maxAttempts) {
        // Retry with exponential backoff (500ms, 1s, 2s, etc.)
        const delay = Math.min(500 * Math.pow(1.5, attempts - 1), 3000);
        timeoutId = setTimeout(initializeGoogle, delay);
      } else {
        // All retries exhausted - Google script failed to load
        setError('Failed to load Google Sign-In. Please refresh the page or check if ad blockers are interfering.');
      }
    };

    // Start initialization
    initializeGoogle();

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [googleClientId, isAuthenticated, loading, handleGoogleCallback]);

  const handleLogout = () => {
    clearAuth();
    setIsAuthenticated(false);
    setUser(null);

    // Also sign out of Google
    if ((window as any).google?.accounts?.id) {
      (window as any).google.accounts.id.disableAutoSelect();
    }
  };

  if (loading) {
    return (
      <div className="page">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="page">
        <div className="auth-container">
          <div className="auth-card">
            <div className="auth-icon" aria-hidden="true">🔐</div>
            <h2>Admin Access</h2>
            <p className="text-muted">Sign in with your authorized Google account</p>

            {error && <div className="error-message" style={{ marginBottom: '1rem' }}>{error}</div>}

            <div id="google-signin-button" style={{ display: 'flex', justifyContent: 'center', marginTop: '1.5rem', minHeight: '44px' }}></div>

            {!buttonRendered && !error && (
              <div style={{ textAlign: 'center', marginTop: '0.5rem' }}>
                <p className="text-muted" style={{ fontSize: '0.85rem' }}>Loading sign-in button...</p>
              </div>
            )}

            <p className="text-muted" style={{ marginTop: '1.5rem', fontSize: '0.8rem' }}>
              Only authorized administrators can access this area.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-wrapper">
      <div className="admin-header" ref={dropdownRef}>
        <button
          className="admin-user-btn"
          onClick={() => setDropdownOpen(!dropdownOpen)}
          aria-expanded={dropdownOpen}
          aria-haspopup="true"
        >
          {user?.picture && (
            <img src={user.picture} alt="" className="admin-user-avatar" />
          )}
          <span className="admin-user-name">{user?.name || user?.email}</span>
          <span className="admin-user-chevron">{dropdownOpen ? '▲' : '▼'}</span>
        </button>
        {dropdownOpen && (
          <div className="admin-dropdown">
            <div className="admin-dropdown-header">
              <span className="admin-dropdown-email">{user?.email}</span>
            </div>
            <button className="admin-dropdown-item" onClick={handleLogout}>
              Sign Out
            </button>
          </div>
        )}
      </div>
      {children}
    </div>
  );
}

export default AdminAuth;
